/**
 * CalculatorButton - Single calculator button with press animation
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';

// ============================================
// TYPES
// ============================================

export type ButtonType = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'del' | 'ok';

export interface CalculatorButtonProps {
  /** Button type */
  type: ButtonType;
  /** Click handler */
  onPress?: (type: ButtonType) => void;
  /** Disabled state */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Button size */
  size?: number;
}

// ============================================
// BUTTON IMAGE MAP
// ============================================

const BUTTON_IMAGES: Record<ButtonType, string> = {
  '0': '/images/calculator/button0.png',
  '1': '/images/calculator/button1.png',
  '2': '/images/calculator/button2.png',
  '3': '/images/calculator/button3.png',
  '4': '/images/calculator/button4.png',
  '5': '/images/calculator/button5.png',
  '6': '/images/calculator/button6.png',
  '7': '/images/calculator/button7.png',
  '8': '/images/calculator/button8.png',
  '9': '/images/calculator/button9.png',
  'del': '/images/calculator/button_del.png',
  'ok': '/images/calculator/button_ok.png',
};

// ============================================
// COMPONENT
// ============================================

export function CalculatorButton({
  type,
  onPress,
  disabled = false,
  className,
  size,
}: CalculatorButtonProps) {
  const [isPressed, setIsPressed] = useState(false);

  const handleMouseDown = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    if (!disabled && isPressed) {
      setIsPressed(false);
      onPress?.(type);
    }
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
  };

  const handleTouchStart = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handleTouchEnd = () => {
    if (!disabled && isPressed) {
      setIsPressed(false);
      onPress?.(type);
    }
  };

  // Only apply fixed size if provided, otherwise let CSS handle it
  const sizeStyle = size ? { width: size, height: size } : {};

  return (
    <button
      type="button"
      className={cn('calculator-button', isPressed && 'calculator-button--pressed', className)}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      disabled={disabled}
      style={sizeStyle}
    >
      <img
        src={BUTTON_IMAGES[type]}
        alt={type}
        className="calculator-button__image"
        draggable={false}
      />
    </button>
  );
}
